import { createContext, useContext, useState, useMemo } from 'react';
import { config } from '../config/runtimeConfig';

/**
 * Duty Free Context
 * Управляет состоянием Duty Free режима для показа цен без VAT
 *
 * Duty Free цены рассчитываются как: цена / (1 + VAT_RATE)
 * Для Кипра VAT составляет 19%
 */
const DutyFreeContext = createContext(null);

/**
 * Расчитать Duty Free цену из обычной цены (legacy — расчёт по VAT)
 * @param {number} price - обычная цена с VAT
 * @param {number} vatRate - VAT ставка (по умолчанию 0.19 для Кипра)
 * @returns {number} - Duty Free цена без VAT
 * @deprecated Используй getDutyFreePrice(car) для получения цены из инвентаря
 */
export const calculateDutyFreePrice = (price, vatRate = 0.19) => {
  if (!price || price <= 0) return price;
  // Duty Free = цена с VAT / (1 + VAT_rate)
  return Math.round(price / (1 + vatRate));
};

/**
 * Получить Duty Free цену для автомобиля из данных инвентаря
 * Если авто имеет dutyFree=true и dutyFreePrice > 0, возвращает dutyFreePrice из инвентаря.
 * Иначе возвращает обычную цену (без изменений).
 *
 * @param {Object} car - объект автомобиля с полями { price, dutyFree, dutyFreePrice }
 * @returns {number} - Duty Free цена из инвентаря или обычная цена
 */
export const getDutyFreePrice = (car) => {
  if (!car) return 0;
  if (car.dutyFree && car.dutyFreePrice > 0) {
    return car.dutyFreePrice;
  }
  return car.price || 0;
};

/**
 * Форматировать цену для отображения
 * @param {number} price - цена
 * @param {boolean} isDutyFree - является ли цена Duty Free
 * @returns {string} - отформатированная цена
 */
export const formatPrice = (price, isDutyFree = false) => {
  if (!price || price <= 0) return 'Contact for price';
  const prefix = isDutyFree ? 'DF ' : '';
  return `${prefix}€${price.toLocaleString()}`;
};

/**
 * Расчитать ежемесячный платёж (PCM) по формуле balloon loan
 *
 * Структура: N ежемесячных платежей + 1 финальный lump sum (% от цены).
 * Пример: 23 monthly + 1 lump sum (50%) на 24-й месяц, 10.88% APR.
 *
 * PMT = ceil((Price - Balloon * (1+r)^(-balloonMonth)) / annuityFactor)
 * где annuityFactor = (1 - (1+r)^(-installmentMonths)) / r
 *     Balloon = Price * balloonPercent
 *
 * Проверка: price=4450 → PMT=129, price=9950 → PMT=288
 * (мат. точное значение для 9950 — 287.48, Math.ceil даёт 288; выбор
 *  округления вверх намеренный — нельзя занизить платёж клиенту).
 *
 * @param {number} price - цена авто
 * @param {number} installmentMonths - количество ежемесячных платежей (по умолчанию 23)
 * @param {number} annualRate - годовая процентная ставка (по умолчанию 0.1088 = 10.88%)
 * @param {number} balloonPercent - доля lump sum от цены (по умолчанию 0.50 = 50%)
 * @param {number} balloonMonth - месяц выплаты lump sum (по умолчанию 24)
 * @returns {number} - ежемесячный платёж, округлённый вверх
 */
export const calculateMonthlyPayment = (price, installmentMonths = 23, annualRate = 0.1088, balloonPercent = 0.50, balloonMonth = 24) => {
  if (!price || price <= 0) return 0;

  const r = annualRate / 12;
  if (r === 0) return Math.ceil(price * (1 - balloonPercent) / installmentMonths);

  const balloon = price * balloonPercent;
  const annuityFactor = (1 - Math.pow(1 + r, -installmentMonths)) / r;
  return Math.ceil((price - balloon * Math.pow(1 + r, -balloonMonth)) / annuityFactor);
};

/**
 * Максимальная сумма финансирования (BF balloon loan).
 * Финансируется не больше этой суммы — всё, что сверху, обязан внести клиент
 * первоначальным взносом (deposit).
 */
export const BF_MAX_FINANCING_DEFAULT = 12000;

/**
 * Минимальный первоначальный взнос (deposit) для авто.
 *
 * Финансирование капается на maxFinancing (по умолчанию €12,000). Всё, что
 * дороже, обязан покрыть deposit, поэтому minDeposit = price - maxFinancing
 * (но не меньше 0 — дешёвые авто финансируются целиком, deposit не обязателен).
 *
 * @param {number} price - цена авто
 * @param {number} maxFinancing - потолок финансирования (по умолчанию 12000)
 * @returns {number} - минимальный deposit (>= 0)
 */
export const getMinDeposit = (price, maxFinancing = BF_MAX_FINANCING_DEFAULT) => {
  if (!price || price <= 0) return 0;
  return Math.max(0, price - maxFinancing);
};

/**
 * Сумма финансирования при заданном deposit.
 *
 * financed = price - deposit, где deposit зажат в [minDeposit, price].
 * По построению financed <= maxFinancing (deposit >= price - maxFinancing).
 * Когда deposit увеличивается сверх минимума, финансирование (и PCM) падает.
 *
 * @param {number} price - цена авто
 * @param {number} deposit - выбранный первоначальный взнос
 * @param {number} maxFinancing - потолок финансирования (по умолчанию 12000)
 * @returns {number} - финансируемая сумма (>= 0)
 */
export const getFinancedAmount = (price, deposit, maxFinancing = BF_MAX_FINANCING_DEFAULT) => {
  if (!price || price <= 0) return 0;
  const minDeposit = getMinDeposit(price, maxFinancing);
  const clampedDeposit = Math.min(Math.max(deposit ?? minDeposit, minDeposit), price);
  return price - clampedDeposit;
};

export const DutyFreeProvider = ({ children }) => {
  // Duty Free switcher всегда начинается в выключенном состоянии (OFF)
  // SHOW_DUTY_FREE_PRICES определяет только показывать ли UI элемент, а не его состояние
  const [isDutyFree, setIsDutyFree] = useState(false);

  // VAT ставка из конфига (по умолчанию 19% для Кипра)
  const vatRate = config.DUTY_FREE_VAT_RATE || 0.19;

  // Функции для управления состоянием
  const setDutyFree = (value) => setIsDutyFree(value);
  const toggleDutyFree = () => setIsDutyFree(prev => !prev);

  // Текстовые конфигурации из runtimeConfig (читаем напрямую через getters)
  const labelText = config.DUTY_FREE_LABEL;
  const tooltipTitle = config.DUTY_FREE_TOOLTIP_TITLE;
  const tooltipText = config.DUTY_FREE_TOOLTIP_TEXT;
  const buttonText = config.DUTY_FREE_BUTTON_TEXT;
  const priceLabel = config.DUTY_FREE_PRICE_LABEL;

  // Government fee text
  const govFeeText = config.DUTY_FREE_GOV_FEE_TEXT;

  // British Forces Exclusive Package конфигурации
  const bfPackageTitle = config.BF_PACKAGE_TITLE;
  const bfPackagePcmText = config.BF_PACKAGE_PCM_TEXT;
  const bfPackageBadge1 = config.BF_PACKAGE_BADGE_1;
  const bfPackageBadge2 = config.BF_PACKAGE_BADGE_2;
  const bfPackageBadge3 = config.BF_PACKAGE_BADGE_3;
  const bfPackageDisclaimer = config.BF_PACKAGE_DISCLAIMER;
  const bfPackageCtaText = config.BF_PACKAGE_CTA_TEXT;
  const bfLoanTermMonths = config.BF_LOAN_TERM_MONTHS;
  const bfAnnualRate = config.BF_ANNUAL_RATE;
  const bfBalloonPercent = config.BF_BALLOON_PERCENT;
  const bfBalloonMonth = config.BF_BALLOON_MONTH;
  const bfMaxFinancing = config.BF_MAX_FINANCING;
  const bfBadgeColor = config.BF_BADGE_COLOR;
  const bfSliderColor = config.BF_SLIDER_COLOR;

  // Создаем value объект для контекста
  const value = useMemo(() => ({
    isDutyFree,
    setIsDutyFree: setDutyFree,
    toggleDutyFree,
    vatRate,
    calculateDutyFree: (car) => getDutyFreePrice(car),
    formatPrice: (price) => formatPrice(price, isDutyFree),
    calculateMonthlyPayment: (price) => calculateMonthlyPayment(price, bfLoanTermMonths, bfAnnualRate, bfBalloonPercent, bfBalloonMonth),
    // Text configurations
    labelText,
    tooltipTitle,
    tooltipText,
    buttonText,
    priceLabel,
    govFeeText,
    // British Forces Exclusive Package configurations
    bfPackageTitle,
    bfPackagePcmText,
    bfPackageBadge1,
    bfPackageBadge2,
    bfPackageBadge3,
    bfPackageDisclaimer,
    bfPackageCtaText,
    bfLoanTermMonths,
    bfAnnualRate,
    bfBalloonPercent,
    bfBalloonMonth,
    bfMaxFinancing,
    bfBadgeColor,
    bfSliderColor,
  }), [isDutyFree, vatRate, labelText, tooltipTitle, tooltipText, buttonText, priceLabel, govFeeText, bfPackageTitle, bfPackagePcmText, bfPackageBadge1, bfPackageBadge2, bfPackageBadge3, bfPackageDisclaimer, bfPackageCtaText, bfLoanTermMonths, bfAnnualRate, bfBalloonPercent, bfBalloonMonth, bfMaxFinancing, bfBadgeColor, bfSliderColor]);

  return (
    <DutyFreeContext.Provider value={value}>
      {children}
    </DutyFreeContext.Provider>
  );
};

export const useDutyFree = () => {
  const context = useContext(DutyFreeContext);
  if (!context) {
    console.warn('[useDutyFree] DutyFreeContext not found, using default values');
    return {
      isDutyFree: false,
      setIsDutyFree: () => {},
      toggleDutyFree: () => {},
      vatRate: 0.19,
      calculateDutyFree: (car) => getDutyFreePrice(car),
      formatPrice: (price) => formatPrice(price, false),
      calculateMonthlyPayment: (price) => calculateMonthlyPayment(price, 23, 0.1088, 0.50, 24),
      // Text configurations fallback
      labelText: 'Duty free prices',
      tooltipTitle: 'Duty free prices',
      tooltipText: 'For British Forces/Serving Personnel. Prices shown exclude 19% VAT.',
      buttonText: 'Show Duty Free Price',
      priceLabel: 'DF',
      govFeeText: '* Government fixed fee 150€',
      // British Forces Exclusive Package fallback
      bfPackageTitle: 'British Forces Exclusive Package',
      bfPackagePcmText: 'From only €{pcm} PCM*',
      bfPackageBadge1: 'ZERO DOWN OPTION**',
      bfPackageBadge2: 'BUY-BACK GUARANTEE',
      bfPackageBadge3: 'LOW CREDIT SCORE ACCEPTED',
      bfPackageDisclaimer: '* Finance based on DUTY FREE price for personal loan through Eurobank',
      bfPackageCtaText: 'Request an offer',
      bfLoanTermMonths: 23,
      bfAnnualRate: 0.1088,
      bfBalloonPercent: 0.50,
      bfBalloonMonth: 24,
      bfMaxFinancing: BF_MAX_FINANCING_DEFAULT,
      bfBadgeColor: null,
      bfSliderColor: null,
    };
  }
  return context;
};

export default DutyFreeContext;
